import { FC, useState } from 'react';
import { Box, Paper, Stack, Text, Badge, Skeleton } from '@mantine/core';
import { IconLeaf } from '@tabler/icons-react';
import type { MenuItem } from '../types';
import { ViewMenuItemModal } from './ViewMenuItemModal';
import { useInView } from 'react-intersection-observer';

interface MenuItemCardProps {
  item: MenuItem;
}

export const MenuItemCard: FC<MenuItemCardProps> = ({ item }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '50px' });

  return (
    <>
      <Paper
        ref={ref}
        shadow="sm"
        radius="md"
        withBorder
        style={{
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'transform 0.2s, box-shadow 0.2s',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--mantine-color-body)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '';
        }}
        onClick={() => setModalVisible(true)}
      >
        {item.image && (
          <Box style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
            {(!imageLoaded || !inView) && (
              <Skeleton height={200} width="100%" />
            )}
            {inView && (
              <img
                src={item.image}
                alt={item.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s',
                  display: imageLoaded ? 'block' : 'none',
                }}
                onLoad={() => setImageLoaded(true)}
                onMouseEnter={(e) => {
                  if (imageLoaded) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            )}
            {item.isVegetarian && (
              <Badge
                color="green"
                variant="filled"
                leftSection={<IconLeaf size={12} />}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 1,
                }}
              >
                Veg
              </Badge>
            )}
          </Box>
        )}

        <Stack gap="xs" p="md" style={{ flex: 1 }}>
          <Text fw={700} size="lg" lineClamp={1}>
            {item.name}
          </Text>
          
          <Text size="xl" fw={700} c="orange.7">
            Rs. {item.price}
          </Text>
          
          {item.description && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {item.description}
            </Text>
          )}
          
          {item.preparationTime && (
            <Text size="xs" c="dimmed">
              ⏱ {item.preparationTime} min
            </Text>
          )}
        </Stack>
      </Paper>

      <ViewMenuItemModal
        menuItem={item}
        opened={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
};